import { SmrStaySuggestionItem, StaySuggestionSearch } from '@/misc/types'
import { DependenciesType, SuggestedDependency } from '@/store/modules/stay/smr/types'

export const MAX_COGNITIVE_DEPENDENCY_VALUE = 8,
  MAX_PHYSICAL_DEPENDENCY_VALUE = 16,
  MAX_DEPENDENCY_VALUE = 4,
  MIN_COGNITIVE_DEPENDENCY_VALUE = 2,
  MIN_PHYSICAL_DEPENDENCY_VALUE = 4,
  MIN_DEPENDENCY_VALUE = 1

export interface ExistingValue {
  lowValue: string
  highValue: string
  formattedValue: string
}

interface Result {
  formattedValue: string
  isDifferent: boolean
  addToSearch: boolean
}

export function getMaxDependencyValue(dependencyType: DependenciesType): number {
  switch (dependencyType) {
    case DependenciesType.PHYSICAL:
      return MAX_PHYSICAL_DEPENDENCY_VALUE
    case DependenciesType.COGNITIVE:
      return MAX_COGNITIVE_DEPENDENCY_VALUE
    default:
      return MAX_DEPENDENCY_VALUE
  }
}

export function getMinDependencyValue(dependencyType: DependenciesType): number {
  switch (dependencyType) {
    case DependenciesType.PHYSICAL:
      return MIN_PHYSICAL_DEPENDENCY_VALUE
    case DependenciesType.COGNITIVE:
      return MIN_COGNITIVE_DEPENDENCY_VALUE
    default:
      return MIN_DEPENDENCY_VALUE
  }
}

export function parseRange(value: string): [number, number] | null {
  if (!value) return null
  const [lowStr, highStr] = value.split('_')
  const low = Number(lowStr)
  const high = highStr ? Number(highStr) : low
  return (isNaN(low) || isNaN(high)) ? null : [low, high]
}

export function isRangeValid(depType: DependenciesType, low: number, high: number): boolean {
  const min = getMinDependencyValue(depType)
  const max = getMaxDependencyValue(depType)
  return low >= min && low <= max && high >= min && high <= max
}

export function getFormattedValue(
  existing: ExistingValue | null,
  lowNum: number,
  highNum: number
): Result {
  if (!existing) {
    return {
      formattedValue: lowNum === highNum ? `${highNum}` : `${lowNum}-${highNum}`,
      isDifferent: true,
      addToSearch: true,
    }
  }

  const existingLow = Number(existing.lowValue)
  const existingHigh = Number(existing.highValue)

  const isExistingInterval = existingLow !== existingHigh
  const isNewInterval = lowNum !== highNum

  // Both single values
  if (!isExistingInterval && !isNewInterval) {
    return {
      formattedValue: `${Math.max(existingHigh, highNum)}`,
      isDifferent: existingHigh !== highNum,
      addToSearch: highNum >= existingHigh,
    }
  }

  // Existing interval + new single value
  if (isExistingInterval && !isNewInterval) {
    if (highNum >= existingHigh) {
      return { formattedValue: `${highNum}`, isDifferent: true, addToSearch: true }
    }
    return { formattedValue: `${existingLow}-${existingHigh}`, isDifferent: false, addToSearch: false }
  }

  // Existing single value + new interval
  if (!isExistingInterval && isNewInterval) {
    if (highNum > existingHigh) {
      return { formattedValue: `${lowNum}-${highNum}`, isDifferent: true, addToSearch: true }
    }
    return { formattedValue: `${existingHigh}`, isDifferent: false, addToSearch: false }
  }

  // Both intervals
  if (isExistingInterval && isNewInterval) {
    if (highNum > existingHigh) {
      return { formattedValue: `${lowNum}-${highNum}`, isDifferent: true, addToSearch: true }
    }
    if (highNum === existingHigh) {
      // both intervals with same high: shrink left side
      const newLow = Math.max(existingLow, lowNum)
      return {
        formattedValue: `${newLow}-${existingHigh}`,
        isDifferent: newLow !== existingLow,
        addToSearch: lowNum >= existingLow,
      }
    }
    return { formattedValue: `${existingLow}-${existingHigh}`, isDifferent: false, addToSearch: false }
  }

  // Fallback
  return {
    formattedValue: isExistingInterval ? `${existingLow}-${existingHigh}` : `${existingHigh}`,
    isDifferent: false,
    addToSearch: false,
  }
}

export function getSuggestedDependencies(staySuggestions: SmrStaySuggestionItem[]): {
  values: Record<DependenciesType, SuggestedDependency | null>
  searches: Record<DependenciesType, StaySuggestionSearch[]>
} {
  const values: Record<DependenciesType, SuggestedDependency | null> = {} as Record<
    DependenciesType,
      SuggestedDependency | null
  >
  const searches: Record<DependenciesType, StaySuggestionSearch[]> = {} as Record<
    DependenciesType,
    StaySuggestionSearch[]
  >

  for (const depType of Object.values(DependenciesType)) {
    values[depType] = null
    searches[depType] = []
  }

  // The backend groups suggestion items by (type, value) — one item per distinct dependency_value
  // shared by one or more searches (see SuggestionRepository::getSuggestionsForSmrStay) — so each
  // item here plays the role a single search's criterion used to play, merged in the same way.
  for (const item of staySuggestions ?? []) {
    const depType = Object.values(DependenciesType).find((type) => type === item.type)
    const range = parseRange(item.reference)
    if (!depType || !range) continue

    const [low, high] = range
    if (!isRangeValid(depType, low, high)) continue

    const { formattedValue, isDifferent, addToSearch } = getFormattedValue(values[depType], low, high)
    const [lowValue, highValue = lowValue] = formattedValue.split('-').map((v) => v.trim())
    values[depType] = { lowValue, highValue, formattedValue }

    if (!addToSearch) continue

    if (isDifferent) {
      // Copy: item.searches must not become an alias of searches[depType], which a later
      // iteration's accumulate branch below may push into (this would otherwise mutate the raw
      // suggestion item itself).
      searches[depType] = [...item.searches]
    } else {
      searches[depType].push(...item.searches)
    }
  }

  return { values, searches }
}